import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Droplets, ArrowUp } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ea404883-7061-4aba-83d0-2d4ab5ad0223/artifacts/ffswoxpp_69d6c27f-288c-44ba-8ef2-7664150a7535.jpeg";

export const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-slate-900 text-white pt-20 pb-8" data-testid="footer">
      {/* Wave decoration */}
      <div className="absolute top-0 left-0 right-0 overflow-hidden h-16 -translate-y-full">
        <svg 
          viewBox="0 0 1200 120" 
          preserveAspectRatio="none" 
          className="w-full h-full"
        >
          <path 
            d="M0,0 C150,90 350,0 500,50 C650,100 800,10 1000,80 C1100,110 1150,90 1200,90 L1200,120 L0,120 Z" 
            fill="#0f172a"
          />
        </svg>
      </div>

      {/* Floating bubbles decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-sky-500/5 border border-sky-500/10"
            style={{
              width: Math.random() * 100 + 50,
              height: Math.random() * 100 + 50,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 5 + Math.random() * 3,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.5,
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img 
                src={LOGO_URL} 
                alt="Piscinas Genovés" 
                className="h-14 w-14 object-contain rounded-xl bg-white p-1"
              />
              <div>
                <span className="font-manrope font-bold text-xl text-sky-400">Piscinas</span>
                <span className="font-manrope font-bold text-xl text-white ml-1">Genovés</span>
              </div>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Expertos en construcción, mantenimiento y reparación de piscinas en la Costa Brava. 
              Tu oasis mediterráneo está en buenas manos.
            </p>
            <div className="flex items-center gap-2 text-sky-400">
              <Droplets className="w-5 h-5" />
              <span className="text-sm font-medium">Especialistas en agua</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-manrope font-bold text-lg mb-6">Enlaces</h4>
            <ul className="space-y-3">
              {[
                { name: 'Inicio', path: '/' },
                { name: 'Servicios', path: '/servicios' },
                { name: 'Zona de Cobertura', path: '/zona' },
                { name: 'Sobre Nosotros', path: '/nosotros' },
                { name: 'Contacto', path: '/contacto' },
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-slate-400 hover:text-sky-400 transition-colors text-sm flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-sky-500/50 group-hover:bg-sky-400 transition-colors" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-manrope font-bold text-lg mb-6">Servicios</h4>
            <ul className="space-y-3">
              {[
                'Construcción de Piscinas',
                'Mantenimiento',
                'Reparaciones',
                'Tratamiento de Aguas',
                'Climatización',
              ].map((service) => (
                <li key={service}>
                  <Link 
                    to="/servicios"
                    className="text-slate-400 hover:text-sky-400 transition-colors text-sm flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-sky-500/50 group-hover:bg-sky-400 transition-colors" />
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-manrope font-bold text-lg mb-6">Contacto</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:+34634060878"
                  className="flex items-start gap-3 text-slate-400 hover:text-sky-400 transition-colors group"
                  data-testid="footer-phone"
                >
                  <Phone className="w-5 h-5 mt-0.5 text-sky-500" />
                  <div>
                    <span className="text-sm block">+34 634 06 08 78</span>
                    <span className="text-xs text-slate-500">Llámanos</span>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:genovespiscinas@gmail.com"
                  className="flex items-start gap-3 text-slate-400 hover:text-sky-400 transition-colors group"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5 mt-0.5 text-sky-500" />
                  <div>
                    <span className="text-sm block">genovespiscinas@gmail.com</span>
                    <span className="text-xs text-slate-500">Escríbenos</span>
                  </div>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-slate-400">
                  <MapPin className="w-5 h-5 mt-0.5 text-sky-500 flex-shrink-0" />
                  <div>
                    <span className="text-sm block">Puigmal 41/44 Local 3</span>
                    <span className="text-sm block">Empuriabrava 17487</span>
                  </div>
                </div>
              </li>
              <li>
                <div className="flex items-start gap-3 text-slate-400">
                  <Clock className="w-5 h-5 mt-0.5 text-sky-500" />
                  <div>
                    <span className="text-sm block">Lun - Vie: 9:00 - 19:00</span>
                    <span className="text-sm block">Sáb: 9:00 - 14:00</span>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm text-center md:text-left">
            © {new Date().getFullYear()} Piscinas Genovés. Todos los derechos reservados.
          </p>
          
          <button
            onClick={scrollToTop}
            data-testid="scroll-to-top"
            className="flex items-center gap-2 text-slate-400 hover:text-sky-400 transition-colors group"
          >
            <span className="text-sm">Volver arriba</span>
            <div className="w-10 h-10 rounded-full border border-slate-700 flex items-center justify-center group-hover:border-sky-500 group-hover:bg-sky-500/10 transition-all">
              <ArrowUp className="w-4 h-4" />
            </div>
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
